import React, { useState } from "react";
import { Product } from "@/types/types";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/IconButton";
import { ArrowRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ArrowRightOutlined";
import { TickOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import { Badge } from "@JohnLewisPartnership/jl-design-system/dist/components/badge/Badge";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { CompetitorList } from "./CompetitorList";
import styles from "./PricePromiseUIV2.module.scss";

interface PricePromiseProps {
  product: Product;
  isInGroup?: boolean;
  isJohnLewisProduct?: boolean;
}

const PricePromiseUIV2: React.FC<PricePromiseProps> = ({
  product,
  isInGroup = false,
  isJohnLewisProduct = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const TICK_SIZE = "large";

  function handleDrawerButtonClick() {
    setIsOpen(true);
    dispatchMonetateQEvent("WPDP | Price Promise Info Displayed");
  }

  function handlePricePromiseURLClick(
    e: React.MouseEvent<HTMLAnchorElement>,
  ): void {
    e.preventDefault();
    dispatchMonetateQEvent("pdp:price-promise:url-displayed");
    const hrefTarget = e.currentTarget.getAttribute("href") || "";
    window.location.href = hrefTarget;
  }

  return (
    <>
      <div
        className={`${styles.pricePromiseContainer} ${isInGroup ? styles.pricePromiseGroupContainer : ""}`}
        data-testid="redesigned-price-promise-container"
      >
        <div
          data-testid="price-promise-badge-wrapper"
          onClick={() => {
            dispatchMonetateQEvent("pdp:price-promise:badge:click");
          }}
        >
          <Badge className={styles.badge} label="Price promise" />
        </div>

        <IconButton
          aria-label="How our price promise works"
          IconComponent={ArrowRightOutlined}
          size="small"
          variant="clear-dark"
          onClick={handleDrawerButtonClick}
        />
      </div>
      <Drawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className={styles.drawer}
      >
        <h2 className={styles.drawerTitle}>Our price promise</h2>
        <div className={styles.drawerBody}>
          {isJohnLewisProduct ? (
            <>
              <h3 className={styles.subHeading}>We match our own prices too</h3>
              <ul className={styles.tickList}>
                <li>
                  <TickOutlined size={TICK_SIZE} /> If we drop the price up to 7
                  days after you buy, you can request a refund for the
                  difference.
                </li>
              </ul>
            </>
          ) : (
            <>
              <h3 className={styles.subHeading}>
                We&apos;re already price matching this item
              </h3>
              <ul className={styles.tickList}>
                <li>
                  <TickOutlined size={TICK_SIZE} /> We automatically check our
                  prices against 25 top retailers, and if we find a better price
                  we&apos;ll match it
                </li>
                <li>
                  <TickOutlined size={TICK_SIZE} /> If they drop the price up to
                  7 days after you buy, you can request a refund for the
                  difference. You can also do this if we drop the price
                  ourselves.
                </li>
                <li>
                  <TickOutlined size={TICK_SIZE} /> We even match promotions and
                  seasonal offers, exclusions apply.
                </li>
              </ul>
            </>
          )}
          <a
            onClick={handlePricePromiseURLClick}
            className={styles.moreInfoLink}
            href="https://www.johnlewis.com/customer-services/prices-and-payment/price-promise?source=pricepromisedrawer"
          >
            {isJohnLewisProduct ? (
              "How to request a refund"
            ) : (
              <>How our price promise works</>
            )}
          </a>
          {!isJohnLewisProduct && <CompetitorList product={product} />}
        </div>
        <footer className={styles.drawerFooter}>
          Never knowingly undersold
        </footer>
      </Drawer>
    </>
  );
};

export default PricePromiseUIV2;
